import {
    PrinterAppId,
    PrinterCustomerInformationModeSaleBlock,
    PrinterRowData,
} from "../types";
import { ModeOfSaleRow, SalesModes } from "../stores/orchestrator";

export type SalesModeApiItem = {
    "Mode of sale": string;
    support_vente: string[];
};

/**
 * Builds the mode-of-sale rows shown in the kitchen Print* components for one
 * field, merging the API sale modes (filtered by `appId`) with the rows already
 * saved on the row. Faithful, typed replacement for the inline logic that the
 * KitchenTicket Print* components used to duplicate.
 */
export function buildModeRows(
    salesModesData: SalesModeApiItem[],
    existing: ModeOfSaleRow[] | undefined,
    appId: PrinterAppId,
    defaultState = false
): ModeOfSaleRow[] {
    const saleModes = salesModesData.map((saleMode) => ({
        saleSupports:
            appId === "" || saleMode.support_vente.includes(appId)
                ? [{ designation: saleMode["Mode of sale"], state: defaultState }]
                : [],
    }));

    let rowDataSales: ModeOfSaleRow[] =
        existing !== undefined ? [...existing] : [];
    saleModes.forEach((el) => {
        el.saleSupports.forEach((elt) => {
            rowDataSales = rowDataSales.filter(
                (support) => support.designation !== elt.designation
            );
        });
    });

    const seen: string[] = [];
    let result: ModeOfSaleRow[] = [];
    saleModes.forEach((el) => {
        if (existing !== undefined) {
            existing.forEach((element) => {
                seen.push(element.designation);
                result.push(element);
            });
            el.saleSupports.forEach((elt) => {
                if (!seen.includes(elt.designation)) {
                    result.push(elt);
                }
            });
        } else {
            el.saleSupports.forEach((elt) => {
                result.push(elt);
            });
        }
    });

    result = [...new Set(result)];
    for (const sale of rowDataSales) {
        result = result.filter((el) => el.designation !== sale.designation);
    }
    return result;
}

function buildSaleSupports(
    responseData: SalesModeApiItem[],
    appId: PrinterAppId
): { saleSupports: ModeOfSaleRow[] }[] {
    return responseData
        .filter(
            (saleMode) => appId === "" || saleMode.support_vente.includes(appId)
        )
        .map((saleMode) => ({
            saleSupports: [
                {
                    designation: saleMode["Mode of sale"],
                    state: false,
                },
            ],
        }));
}

function mergeWithExisting(
    saleModes: { saleSupports: ModeOfSaleRow[] }[],
    existingList: ModeOfSaleRow[] | undefined
): ModeOfSaleRow[] {
    const rowDataSales: ModeOfSaleRow[] = existingList ? [...existingList] : [];

    const supportDesignations = new Set<string>();
    saleModes.forEach((el) =>
        el.saleSupports.forEach((elt) => {
            supportDesignations.add(elt.designation);
        })
    );
    const filteredRowData = rowDataSales.filter((o) =>
        supportDesignations.has(o.designation)
    );

    const result: ModeOfSaleRow[] = [];
    const includedDesignations = new Set<string>();

    filteredRowData.forEach((el) => {
        result.push(el);
        includedDesignations.add(el.designation);
    });

    saleModes.forEach((el) => {
        el.saleSupports.forEach((elt) => {
            if (!includedDesignations.has(elt.designation)) {
                result.push(elt);
                includedDesignations.add(elt.designation);
            }
        });
    });

    return result;
}

export function buildSalesModes(
    responseData: SalesModeApiItem[],
    rowData: PrinterRowData,
    appId: PrinterAppId
): SalesModes {
    const saleModes = buildSaleSupports(responseData, appId);

    const rawCustomerTicket = rowData["customer_ticket"];
    const customerTicketBlock =
        typeof rawCustomerTicket === "object" ? rawCustomerTicket : undefined;
    const rawKitchenTicket = rowData["Kitchen_ticket"];
    const kitchenTicketBlock =
        typeof rawKitchenTicket === "object" ? rawKitchenTicket : undefined;

    const customerInfo: PrinterCustomerInformationModeSaleBlock =
        kitchenTicketBlock?.customer_information_mode_sale ?? {};

    return {
        customerTicket: mergeWithExisting(
            saleModes,
            customerTicketBlock?.dateTimeDeliveryModeSale
        ),
        kitchenTicket: mergeWithExisting(
            saleModes,
            kitchenTicketBlock?.ticketKitchenModeSale
        ),
        salesCustomerAddressModeSale: mergeWithExisting(
            saleModes,
            kitchenTicketBlock?.customerAddressModeSale
        ),
        customerTicketSalesModes: mergeWithExisting(
            saleModes,
            customerTicketBlock?.customerTicketSalesModes
        ),
        customerInformationName: mergeWithExisting(
            saleModes,
            customerInfo.customerInformationModeSaleName
        ),
        customerInformationNumTel: mergeWithExisting(
            saleModes,
            customerInfo.customerInformationModeSaleNumTel
        ),
        customerInformationAddress: mergeWithExisting(
            saleModes,
            customerInfo.customerInformationModeSaleAdress
        ),
        customerInformationCity: mergeWithExisting(
            saleModes,
            customerInfo.customerInformationModeSaleCity
        ),
        customerInformationCompany: mergeWithExisting(
            saleModes,
            customerInfo.customerInformationModeSaleCompany
        ),
        customerInformationCode: mergeWithExisting(
            saleModes,
            customerInfo.customerInformationModeSaleCode
        ),
    };
}
